(() => {
  if (window.vhalamStockFirst || !location.pathname.startsWith('/collections/')) return;

  /** @param {HTMLElement} results @param {URL} pageUrl @param {AbortSignal} signal @param {HTMLElement} owner */
  async function prepare(results, pageUrl, signal, owner = results) {
    const total = Number(results.dataset.productsCount);
    const size = Number(results.dataset.pageSize);
    const page = Number(results.dataset.currentPage);
    if (!Number.isInteger(total) || !Number.isInteger(size) || size < 1 || !Number.isInteger(page) || page < 1) {
      throw new Error('Invalid stock sorting pagination');
    }
    if (total === 0) return;
    const base = new URL(pageUrl);
    base.searchParams.delete('page');
    base.searchParams.delete('sections');
    base.searchParams.delete('section_id');
    owner.stockFirstCatalogs ||= new Map();
    owner.stockFirstPages ||= new Map();

    async function loadCatalog() {
      const records = [];
      const url = new URL(base);
      url.searchParams.set('view', 'vhalam-stock-data');
      let pages = 1;
      for (let number = 1; number <= pages; number += 1) {
        url.searchParams.set('page', String(number));
        const response = await fetch(url, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Stock data unavailable (${response.status})`);
        const data = await response.json();
        if (!Number.isInteger(data.pages) || data.pages < number || data.count !== total || data.page !== number ||
            !Array.isArray(data.products) || data.products.some((product) => typeof product.id !== 'string' || typeof product.available !== 'boolean')) {
          throw new Error('Invalid stock data');
        }
        pages = data.pages;
        records.push(...data.products);
      }
      if (records.length !== total || new Set(records.map((product) => product.id)).size !== total) {
        throw new Error('Incomplete collection stock data');
      }
      return records.map((product, index) => ({ ...product, sourcePage: Math.floor(index / size) + 1 }))
        .sort((a, b) => Number(b.available) - Number(a.available));
    }

    async function loadSourcePage(number) {
      const url = new URL(base);
      url.searchParams.set('section_id', results.dataset.sectionId);
      url.searchParams.set('page', String(number));
      if (owner.stockFirstPages.has(url.href)) return owner.stockFirstPages.get(url.href);
      const pending = (async () => {
        const response = await fetch(url, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Stock results unavailable (${response.status})`);
        const rendered = new DOMParser().parseFromString(await response.text(), 'text/html');
        const group = [...rendered.querySelectorAll('vhalam-infinite-collection')]
          .find((element) => element.dataset.infiniteScrollContext === results.dataset.infiniteScrollContext);
        if (!group || Number(group.dataset.pageSize) !== size || Number(group.dataset.productsCount) !== total) {
          throw new Error('Missing stock results');
        }
        return [...group.querySelectorAll('.product-list > [data-product-id]')];
      })();
      owner.stockFirstPages.set(url.href, pending);
      try {
        return await pending;
      } catch (error) {
        owner.stockFirstPages.delete(url.href);
        throw error;
      }
    }

    if (!owner.stockFirstCatalogs.has(base.href)) owner.stockFirstCatalogs.set(base.href, loadCatalog());
    let catalog;
    try {
      catalog = await owner.stockFirstCatalogs.get(base.href);
    } catch (error) {
      owner.stockFirstCatalogs.delete(base.href);
      throw error;
    }
    const selected = catalog.slice((page - 1) * size, page * size);
    const sourcePages = [...new Set(selected.map((product) => product.sourcePage))];
    const cards = new Map();
    for (let start = 0; start < sourcePages.length; start += 3) {
      const groups = await Promise.all(sourcePages.slice(start, start + 3).map(loadSourcePage));
      for (const group of groups) {
        for (const card of group) cards.set(card.dataset.productId, card);
      }
    }
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const ordered = selected.map((product) => {
      const card = cards.get(product.id);
      if (!card || card.dataset.available !== String(product.available)) throw new Error('Collection stock changed during loading');
      return document.importNode(card, true);
    });
    const grid = results.querySelector('.product-list');
    if (!grid) throw new Error('Missing product grid');
    grid.replaceChildren(...ordered);
    results.dataset.stockFirstReady = 'true';
  }

  window.vhalamStockFirst = { prepare };
})();
